import { useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import { nanoid } from "nanoid";
import type { LocalizedString, SchemeInfoSection } from "@shared/types/foyer";
import { localeMeta, setLocalized } from "@shared/i18n";
import { tokens } from "../../theme";

/**
 * Section editor with a single-language editing model (design 2a).
 *
 * The previous editor stacked one input per locale for every field, which
 * stopped scaling past two languages. Here the pane carries one "Content
 * language" bar: pick a locale and the whole form shows and edits that one
 * language. Each locale pill carries a status blob (translated / needs review
 * / empty) derived from the data, so the translation state of the entire
 * page reads at a glance.
 *
 * Shared by the Information directory and the Relatives pane - the only
 * difference is the optional per-section embed URL.
 */

// ── translation status ──────────────────────────────────────────────────────

export type LocaleStatus = "done" | "review" | "empty";

/**
 * Read one locale's text without the resolution fallback: an untranslated
 * field must render empty, not silently show the default-locale copy.
 *
 * A plain string is pre-i18n content that belongs to the default locale, so
 * it only ever fills the default-locale input.
 */
function readAt(
  value: LocalizedString | undefined,
  locale: string,
  defaultLocale: string
): string {
  if (!value) return "";
  if (typeof value === "string") return locale === defaultLocale ? value : "";
  return value[locale] ?? "";
}

/** Every translatable field across the sections, flattened. */
function collectFields(sections: SchemeInfoSection[]): LocalizedString[] {
  const out: LocalizedString[] = [];
  for (const sec of sections) {
    out.push(sec.section);
    for (const it of sec.items) {
      out.push(it.label);
      out.push(it.value);
    }
  }
  return out;
}

/**
 * Derive a locale's status from the data rather than storing a flag, so the
 * blobs stay truthful automatically.
 *
 * "Required" means the default locale has copy for that field - there is
 * nothing to translate where the source itself is blank. The default locale
 * is therefore never reported as untranslated.
 */
export function localeStatus(
  sections: SchemeInfoSection[],
  locale: string,
  defaultLocale: string
): LocaleStatus {
  if (locale === defaultLocale) return "done";
  const fields = collectFields(sections);
  let required = 0;
  let filled = 0;
  for (const f of fields) {
    if (readAt(f, defaultLocale, defaultLocale).trim().length === 0) continue;
    required++;
    if (readAt(f, locale, defaultLocale).trim().length > 0) filled++;
  }
  if (required === 0 || filled === required) return "done";
  return filled === 0 ? "empty" : "review";
}

const BLOB_COLOR: Record<LocaleStatus, string> = {
  done: tokens.success,
  review: tokens.warning,
  empty: tokens.inkFaint,
};

// ── content language bar ────────────────────────────────────────────────────

function ContentLanguageBar({
  locales,
  defaultLocale,
  active,
  statusOf,
  onSelect,
}: {
  locales: string[];
  defaultLocale: string;
  active: string;
  statusOf: (locale: string) => LocaleStatus;
  onSelect: (locale: string) => void;
}) {
  const doneCount = locales.filter((l) => statusOf(l) === "done").length;
  return (
    <Stack spacing={1.125}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
      >
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: tokens.inkMuted }}
        >
          Content language
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: tokens.inkFaint }}>
          {locales.length} {locales.length === 1 ? "locale" : "locales"} ·{" "}
          {doneCount} translated
        </Typography>
      </Stack>
      <Box
        role="group"
        aria-label="Content language"
        sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
      >
        {locales.map((code) => {
          const m = localeMeta(code);
          const isDefault = code === defaultLocale;
          const selected = code === active;
          const status = statusOf(code);
          return (
            <ButtonBase
              key={code}
              onClick={() => onSelect(code)}
              aria-pressed={selected}
              aria-label={`${m.native}${isDefault ? " (default)" : ""} - ${
                status === "done"
                  ? "translated"
                  : status === "review"
                    ? "partly translated"
                    : "not translated"
              }`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.875,
                px: 1.5,
                py: 0.875,
                borderRadius: "9px",
                fontSize: 12.5,
                fontWeight: 500,
                border: "1px solid",
                transition: "background-color .12s, border-color .12s",
                ...(selected
                  ? {
                      bgcolor: tokens.ink,
                      color: tokens.surface,
                      borderColor: tokens.ink,
                    }
                  : {
                      bgcolor: tokens.surface,
                      color: tokens.ink,
                      borderColor: tokens.divider,
                      "&:hover": { borderColor: tokens.inkFaint },
                    }),
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 7,
                  height: 7,
                  flex: "none",
                  borderRadius: "50%",
                  bgcolor: BLOB_COLOR[status],
                  opacity: status === "empty" ? 0.55 : 1,
                }}
              />
              {m.native}
              {isDefault && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                    opacity: 0.65,
                  }}
                >
                  default
                </Box>
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
}

// ── single-locale field ─────────────────────────────────────────────────────

/**
 * One input bound to the active locale. When the field has no translation
 * for a non-default locale it renders in the muted "Not translated" style
 * (dashed border, italic placeholder) so gaps are visible without reading
 * every field.
 */
function LocaleField({
  value,
  locale,
  defaultLocale,
  onChange,
  placeholder,
  ariaLabel,
  testId,
}: {
  value: LocalizedString | undefined;
  locale: string;
  defaultLocale: string;
  onChange: (next: LocalizedString) => void;
  placeholder?: string;
  ariaLabel: string;
  testId?: string;
}) {
  const text = readAt(value, locale, defaultLocale);
  const untranslated = text.length === 0 && locale !== defaultLocale;
  return (
    <TextField
      fullWidth
      size="small"
      value={text}
      onChange={(e) =>
        onChange(setLocalized(value, locale, defaultLocale, e.target.value))
      }
      placeholder={untranslated ? "Not translated" : placeholder}
      inputProps={{
        "aria-label": ariaLabel,
        ...(testId ? { "data-testid": `${testId}-${locale}` } : {}),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: untranslated ? tokens.rowAlt : tokens.surface,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderStyle: untranslated ? "dashed" : "solid",
        },
        "& input::placeholder": untranslated
          ? { fontStyle: "italic", opacity: 1, color: tokens.inkFaint }
          : {},
      }}
    />
  );
}

// ── section card ────────────────────────────────────────────────────────────

const CAPTION_SX = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".05em",
  textTransform: "uppercase" as const,
  color: tokens.inkFaint,
};

const ROW_GRID = "1fr 1.35fr 34px";

function SectionCard({
  index,
  section,
  locale,
  defaultLocale,
  showEmbedUrl,
  onPatch,
  onUp,
  onDown,
  onDelete,
}: {
  index: number;
  section: SchemeInfoSection;
  locale: string;
  defaultLocale: string;
  showEmbedUrl: boolean;
  onPatch: (patch: Partial<SchemeInfoSection>) => void;
  onUp: () => void;
  onDown: () => void;
  onDelete: () => void;
}) {
  const isDefault = locale === defaultLocale;
  const missing =
    !isDefault &&
    [
      section.section,
      ...section.items.flatMap((it) => [it.label, it.value]),
    ].some(
      (f) =>
        readAt(f, defaultLocale, defaultLocale).trim().length > 0 &&
        readAt(f, locale, defaultLocale).trim().length === 0
    );

  const setItem = (
    j: number,
    patch: Partial<SchemeInfoSection["items"][number]>
  ) =>
    onPatch({
      items: section.items.map((x, k) => (k === j ? { ...x, ...patch } : x)),
    });

  return (
    <Box
      sx={{
        bgcolor: tokens.surface,
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "14px",
        boxShadow: tokens.shadowCard,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.25,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: tokens.divider,
          bgcolor: tokens.rowAlt,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".09em",
            textTransform: "uppercase",
            color: tokens.inkFaint,
          }}
        >
          Section {index + 1}
        </Typography>
        <Stack direction="row" spacing={0.75}>
          <Tooltip title="Move up">
            <IconButton size="small" aria-label="Move up" onClick={onUp}>
              <ArrowUpwardIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Move down">
            <IconButton size="small" aria-label="Move down" onClick={onDown}>
              <ArrowDownwardIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete section">
            <IconButton
              size="small"
              color="error"
              aria-label="Delete section"
              onClick={onDelete}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack spacing={2.25} sx={{ p: 2.25 }}>
        {missing && (
          <Stack
            direction="row"
            spacing={1.25}
            sx={{
              alignItems: "flex-start",
              px: 1.625,
              py: 1.375,
              bgcolor: tokens.warningTint,
              border: "1px solid",
              borderColor: tokens.warning,
              borderRadius: "9px",
            }}
          >
            <WarningAmberIcon
              fontSize="small"
              sx={{ color: tokens.warning, flex: "none", mt: "1px" }}
            />
            <Typography
              sx={{ fontSize: 12.5, lineHeight: 1.5, color: tokens.ink }}
            >
              Some fields aren&apos;t translated for{" "}
              <strong>{localeMeta(locale).native}</strong> - guests see the{" "}
              <strong>{localeMeta(defaultLocale).native}</strong> default until
              you fill these in.
            </Typography>
          </Stack>
        )}

        <Stack spacing={0.75}>
          <Typography sx={CAPTION_SX}>Section title</Typography>
          <LocaleField
            value={section.section}
            locale={locale}
            defaultLocale={defaultLocale}
            ariaLabel="Section title"
            testId="section-title"
            onChange={(v) => onPatch({ section: v })}
          />
          <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
            Shown as the card heading on the guest page.
          </Typography>
        </Stack>

        {showEmbedUrl && (
          <Stack spacing={0.75}>
            <Typography sx={CAPTION_SX}>
              Activity / embed URL{" "}
              <Box
                component="span"
                sx={{
                  textTransform: "none",
                  letterSpacing: 0,
                  fontWeight: 500,
                  color: tokens.inkFaint,
                }}
              >
                · optional
              </Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="https://…/embed?token={integrationId}"
              value={section.embedUrl ?? ""}
              inputProps={{ "aria-label": "Activity or embed URL" }}
              onChange={(e) =>
                onPatch({ embedUrl: e.target.value.trim() || undefined })
              }
            />
            <Typography
              sx={{ fontSize: 12, lineHeight: 1.5, color: tokens.inkFaint }}
            >
              When set, this section shows this URL in an iframe instead of the
              rows below.{" "}
              <Box
                component="code"
                sx={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 11.5,
                  bgcolor: tokens.rowAlt,
                  color: tokens.inkMuted,
                  px: 0.625,
                  py: "1px",
                  borderRadius: "4px",
                }}
              >
                {"{integrationId}"}
              </Box>{" "}
              is replaced with the room&apos;s token (set per room under Rooms).
            </Typography>
          </Stack>
        )}

        <Box sx={{ height: "1px", bgcolor: tokens.divider }} />

        <Stack spacing={1.25}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink }}
              >
                Rows
              </Typography>
              <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
                {section.items.length}{" "}
                {section.items.length === 1 ? "item" : "items"}
              </Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                onPatch({ items: [...section.items, { label: "", value: "" }] })
              }
              sx={{
                color: tokens.brand,
                bgcolor: tokens.brandTintSoft,
                border: "1px solid",
                borderColor: tokens.brandTint,
                "&:hover": { bgcolor: tokens.brandTint },
              }}
            >
              Add row
            </Button>
          </Stack>

          {section.items.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: ROW_GRID,
                gap: 1.25,
                px: "2px",
              }}
            >
              <Typography sx={CAPTION_SX}>Label</Typography>
              <Typography sx={CAPTION_SX}>Value</Typography>
              <Box />
            </Box>
          )}

          {section.items.map((it, j) => (
            <Box
              key={j}
              sx={{
                display: "grid",
                gridTemplateColumns: ROW_GRID,
                gap: 1.25,
                alignItems: "center",
              }}
            >
              <LocaleField
                value={it.label}
                locale={locale}
                defaultLocale={defaultLocale}
                ariaLabel={`Row ${j + 1} label`}
                onChange={(v) => setItem(j, { label: v })}
              />
              <LocaleField
                value={it.value}
                locale={locale}
                defaultLocale={defaultLocale}
                ariaLabel={`Row ${j + 1} value`}
                onChange={(v) => setItem(j, { value: v })}
              />
              <IconButton
                size="small"
                color="error"
                aria-label={`Delete row ${j + 1}`}
                onClick={() =>
                  onPatch({ items: section.items.filter((_, k) => k !== j) })
                }
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

// ── pane ────────────────────────────────────────────────────────────────────

/** Move helper - returns a new array with item i shifted by `dir` (±1). */
function move<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export default function LocalizedSectionsEditor({
  sections,
  onChange,
  supportedLocales,
  defaultLocale,
  showEmbedUrl = false,
}: {
  sections: SchemeInfoSection[];
  onChange: (next: SchemeInfoSection[]) => void;
  supportedLocales: string[];
  defaultLocale: string;
  /** Information sections carry a per-section embed URL; Relatives doesn't. */
  showEmbedUrl?: boolean;
}) {
  const locales = useMemo(() => {
    const list =
      supportedLocales.length > 0 ? supportedLocales : [defaultLocale];
    // Default first, then the rest in the premise's configured order.
    return [defaultLocale, ...list.filter((l) => l !== defaultLocale)];
  }, [supportedLocales, defaultLocale]);

  const [activeLocale, setActiveLocale] = useState(defaultLocale);
  // The premise's locale set is editable elsewhere; fall back to the default
  // rather than editing a locale that is no longer supported.
  useEffect(() => {
    if (!locales.includes(activeLocale)) setActiveLocale(defaultLocale);
  }, [locales, activeLocale, defaultLocale]);
  const locale = locales.includes(activeLocale) ? activeLocale : defaultLocale;

  const statusOf = (code: string) =>
    localeStatus(sections, code, defaultLocale);

  const patch = (i: number, part: Partial<SchemeInfoSection>) =>
    onChange(sections.map((s, idx) => (idx === i ? { ...s, ...part } : s)));

  return (
    <Stack spacing={2.25}>
      <ContentLanguageBar
        locales={locales}
        defaultLocale={defaultLocale}
        active={locale}
        statusOf={statusOf}
        onSelect={setActiveLocale}
      />
      {sections.map((sec, i) => (
        <SectionCard
          key={sec.id}
          index={i}
          section={sec}
          locale={locale}
          defaultLocale={defaultLocale}
          showEmbedUrl={showEmbedUrl}
          onPatch={(part) => patch(i, part)}
          onUp={() => onChange(move(sections, i, -1))}
          onDown={() => onChange(move(sections, i, 1))}
          onDelete={() => onChange(sections.filter((_, idx) => idx !== i))}
        />
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={() =>
          onChange([
            ...sections,
            { id: nanoid(6), section: "New section", items: [] },
          ])
        }
        sx={{ alignSelf: "flex-start" }}
      >
        Add section
      </Button>
    </Stack>
  );
}
